import {useEffect, useRef} from 'react';
import {scrollerFor} from './useSectionObserver';

/**
 * Llevar la página al ancla del hash — al entrar y cada vez que el hash cambia.
 *
 * En una one-page el navegador NO hace esto solo de forma confiable: los actos se montan después
 * del primer paint (el cliente, el brief, los paneles diferidos), así que cuando el browser busca
 * `#memoria` el elemento todavía no existe o mide 0. De ahí el doble `requestAnimationFrame`: uno
 * para que React termine de aplicar el commit y otro para que el layout se asiente.
 *
 * Detalles que importan:
 *
 * - **El primer scroll es instantáneo, los siguientes son suaves.** Entrar por un deep-link tiene
 *   que aterrizar YA en la sección (un smooth desde arriba de todo es un tour de tres actos que
 *   nadie pidió); navegar de un umbral al siguiente sí se anima, porque la animación es lo que
 *   explica que te moviste dentro de la misma página. `prefers-reduced-motion` apaga las dos.
 *
 * - **Se re-dispara con `location.key`, no sólo con el hash.** Volver a clickear el mismo ancla
 *   deja el hash igual y react-router emite una entrada nueva: sin la key, el segundo click no
 *   hace nada.
 *
 * - **El offset lo pone el CSS** (`scroll-margin-top` sobre los actos). Sumar acá
 *   `var(--header-height)` a mano lo contaría dos veces: `.app-content` ya arranca debajo del
 *   topbar, el mismo desfase que ya nos mordió con los sticky de la 2.1.
 *
 * - **Un intento solo no alcanza, ni para llegar ni para quedarse.** El ancla puede no existir
 *   todavía (`#prompts` vive dentro de la biblioteca, que la llena un fetch) y, una vez que se
 *   aterrizó, los actos de ARRIBA siguen resolviendo los suyos y crecen: el ancla se va de
 *   pantalla sola. Por eso hay una espera acotada antes de aterrizar y un anclaje acotado
 *   después. Las dos ventanas son cortas y se sueltan al primer gesto del usuario: una pantalla
 *   que te devuelve al ancla cada vez que scrolleás es peor que no haber aterrizado nunca.
 */

// Cuánto se espera a un ancla que todavía no está en el DOM. Ninguna red contesta en 120ms, así
// que rendirse en el primer intento es rendirse siempre; esperar para siempre es dejar un timer
// vivo por cada navegación con hash.
const WAIT_MS = 1200;

// Cuánto se sostiene el ancla después de aterrizar. Medido en la app: los actos de arriba resuelven
// sus fetches entre los 300 y los 800ms y crecen ahí, o sea DESPUÉS de que el scroll ya cayó bien.
const HOLD_MS = 1500;

// Un solo reloj para las dos ventanas: más fino no compra nada (el layout se asienta por saltos) y
// más grueso deja ver el salto.
const TICK_MS = 100;

// Debajo de esto es ruido de subpíxel del propio scroll, no un panel que creció.
const DRIFT_PX = 2;

// Gestos con los que el usuario dice "me muevo yo". El evento `scroll` NO sirve: lo dispara también
// nuestro propio `scrollIntoView`, así que soltaría el anclaje en el acto de ponerlo.
const USER_INTENT = ['wheel', 'touchstart', 'keydown', 'pointerdown'];

function prefersReducedMotion() {
    try {
        return typeof window.matchMedia === 'function'
            && window.matchMedia('(prefers-reduced-motion: reduce)').matches === true;
    } catch (e) {
        return false;
    }
}

/**
 * Corre `run` UNA vez, cuando el layout ya se asentó. Devuelve el cancelador.
 *
 * El doble `requestAnimationFrame` es lo que espera al commit de React y al layout, pero no puede
 * ser el único camino: **Chrome congela los rAF en las pestañas ocultas**. Abrir un deep-link en
 * una pestaña de fondo (el Ctrl+click de toda la vida sobre el aviso de la campanita o un CTA del
 * Panel) es un caso normal, y sin respaldo el scroll no ocurría nunca: el usuario cambiaba a esa
 * pestaña y encontraba el Recorrido arriba de todo, sin la sección que había pedido. Verificado en
 * la app con la pestaña en segundo plano: cero ticks de rAF, el ancla intacta.
 *
 * De ahí las tres puertas, la primera que llegue gana: el doble rAF, un timeout de respaldo, y
 * `visibilitychange` para el caso en que la pestaña recién se muestre minutos después.
 */
function onNextLayout(run) {
    let done = false;
    const fire = () => {
        if (done) return;
        done = true;
        run();
    };

    const hasRaf = typeof window.requestAnimationFrame === 'function';
    let outer = null;
    let inner = null;
    if (hasRaf) {
        outer = window.requestAnimationFrame(() => {
            inner = window.requestAnimationFrame(fire);
        });
    }

    // Respaldo por reloj: los timers sí corren en una pestaña oculta (aunque throttleados).
    const timer = window.setTimeout(fire, hasRaf ? 120 : 0);

    // Y si la pestaña estaba oculta, el momento correcto es cuando se muestra: ahí el layout es
    // real y el usuario está mirando.
    const onVisible = () => {
        if (document.visibilityState === 'visible') fire();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
        done = true;
        if (outer !== null) window.cancelAnimationFrame(outer);
        if (inner !== null) window.cancelAnimationFrame(inner);
        window.clearTimeout(timer);
        document.removeEventListener('visibilitychange', onVisible);
    };
}

/** Dónde está el ancla DENTRO del scroller: es lo único comparable entre dos momentos. */
function anchorOffset(el) {
    if (!el || typeof el.getBoundingClientRect !== 'function') return null;
    const top = el.getBoundingClientRect().top;
    // El scroller de la app no es la ventana (`.app-content`), y encima puede moverse solo: un
    // banner de pago o de credenciales que aparece arriba lo empuja entero. Medir contra su caja
    // deja afuera ese movimiento, que no es "el ancla se fue".
    const scroller = scrollerFor(el);
    if (!scroller || typeof scroller.getBoundingClientRect !== 'function') return top;
    return top - scroller.getBoundingClientRect().top;
}

function scrollToAnchor(el, behavior) {
    if (!el || typeof el.scrollIntoView !== 'function') return false;
    try {
        el.scrollIntoView({behavior, block: 'start'});
    } catch (e) {
        /* jsdom y navegadores viejos sin el shape de objeto: no llegar no rompe la página */
    }
    return true;
}

/**
 * Sostiene el ancla mientras la página termina de asentarse.
 *
 * El scroll aterriza a los ~30ms y ahí la página todavía está a medio hacer: entre los 300 y los
 * 800ms los actos de ARRIBA resuelven sus fetches, crecen, y empujan al de abajo fuera de pantalla.
 * El usuario que entró por la campanita a `#bitacora` con `?layers=` termina mirando otro acto, con
 * el strip de "Filtros aplicados" fuera de vista — justo lo que el montaje eager protege.
 *
 * Dos límites, los dos deliberados: la ventana es de un segundo y medio (no "para siempre"), y
 * cualquier gesto del usuario la corta EN EL ACTO. Una pantalla que te devuelve al ancla cada vez
 * que scrolleás está rota de una forma peor.
 */
function holdAnchor(anchorId) {
    let elapsed = 0;
    let baseline = anchorOffset(document.getElementById(anchorId));
    let timer = null;

    const release = () => {
        if (timer !== null) {
            window.clearInterval(timer);
            timer = null;
        }
        USER_INTENT.forEach(evt => document.removeEventListener(evt, release, true));
    };

    // En captura: si el gesto lo atiende un panel de adentro y no burbujea, el anclaje se soltaría
    // igual y seguiríamos peleando con el usuario.
    USER_INTENT.forEach(evt => document.addEventListener(evt, release, true));

    timer = window.setInterval(() => {
        elapsed += TICK_MS;
        const el = document.getElementById(anchorId);
        const offset = anchorOffset(el);
        if (offset !== null) {
            if (baseline === null) {
                baseline = offset;
            } else if (Math.abs(offset - baseline) > DRIFT_PX) {
                scrollToAnchor(el, 'auto');
                // Se re-mide después de corregir: si la corrección no se pudo aplicar entera (el
                // scroller ya está al fondo), el anclaje se conforma con lo que hay en vez de
                // volver a intentarlo cada tick.
                const after = anchorOffset(el);
                if (after !== null) baseline = after;
            }
        }
        if (elapsed >= HOLD_MS) release();
    }, TICK_MS);

    return release;
}

/**
 * Aterriza en el ancla, esperándola si todavía no está, y la sostiene mientras la página se asienta.
 *
 * Lo de esperar no es teórico: `#prompts` vive dentro de la biblioteca de la banda, que la llena un
 * fetch, y `/client/:id/ia/prompts` —ruta viva que redirige a `#prompts`— aterrizaba al usuario
 * arriba de todo, en Alimenta, porque ninguna red contesta en los 120ms del respaldo.
 */
function landOnAnchor(anchorId, behavior) {
    let stopHold = null;
    let waited = 0;
    let timer = null;

    const stopWaiting = () => {
        if (timer !== null) {
            window.clearInterval(timer);
            timer = null;
        }
    };

    const tryLand = () => {
        const el = document.getElementById(anchorId);
        if (!el || !scrollToAnchor(el, behavior)) return false;
        // El anclaje sólo se sostiene sobre un aterrizaje instantáneo. Con `smooth` la página se
        // está moviendo por sí sola: medir "deriva" ahí sería medir la animación que el usuario
        // pidió, y corregirla la cortaría de un salto.
        if (behavior === 'auto') stopHold = holdAnchor(anchorId);
        return true;
    };

    if (!tryLand()) {
        timer = window.setInterval(() => {
            waited += TICK_MS;
            if (tryLand() || waited >= WAIT_MS) stopWaiting();
        }, TICK_MS);
    }

    return () => {
        stopWaiting();
        if (stopHold) stopHold();
    };
}

export function useHashScroll(location, {enabled = true} = {}) {
    // "Primera vez" es la primera corrida ÚTIL, no el primer render: mientras la pantalla espera
    // los módulos no hay anclas a las que ir, y esa pasada no debería gastar el scroll instantáneo.
    const firstRef = useRef(true);
    const hash = (location && location.hash) || '';
    const navKey = (location && location.key) || '';

    useEffect(() => {
        if (!enabled) return undefined;
        const isFirst = firstRef.current;
        firstRef.current = false;

        const anchor = String(hash).replace(/^#/, '');
        if (!anchor) return undefined;

        const behavior = (isFirst || prefersReducedMotion()) ? 'auto' : 'smooth';

        let stopLanding = null;
        const cancelLayout = onNextLayout(() => {
            stopLanding = landOnAnchor(anchor, behavior);
        });

        return () => {
            cancelLayout();
            if (stopLanding) stopLanding();
        };
    }, [hash, navKey, enabled]);
}

export default useHashScroll;
